import React from "react";
import { useDAWStore, FX_DEFS } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import Knob from "./Knob";
import Sampler from "./Sampler";
import EqFive from "./EqFive";
import { MasterWaveView } from "./Visualizers";
import { X, Plus } from "@phosphor-icons/react";

const COLORS = {
  synth: "#22C55E",
  sampler: "#EAB308",
};

function PluginShell({ name, color, gradient, children, onClose, testId }) {
  return (
    <div className="plugin-panel p-4 mb-3" data-testid={testId || `plugin-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#262626]">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-sm" style={{ background: gradient || color, boxShadow: `0 0 8px ${color}80` }} />
          <h3 className="font-display font-bold text-sm tracking-tight">{name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-mono">{name === "Galaxy Synth" || name === "Sampler" ? "instrument" : "fx"}</span>
          {onClose && (
            <button onClick={onClose} className="text-white/40 hover:text-red-400" title="Remove">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ---------- Track-FX edit components (use store + engine to apply live) ---------- */
// v19.16 — makeApply is now context-aware: it routes to either the
// master FX store slot or a specific track's FX slot depending on
// whether `masterFxId` is provided. Every FX editor accepts a
// `masterFxId` prop that forwards through here — previously only the
// Soft Clipper handled the master branch, which is why every OTHER
// master FX plugin's knobs looked dead (they called an undefined
// `updateFxParam(undefined, ...)` and mutated `engine.trackFx[undefined]`).
function makeApply(trackId, fxId, masterFxId) {
  const apply = (param, value) => {
    const state = useDAWStore.getState();
    // Master FX chain — update master slot + master node.
    if (masterFxId) {
      state.updateMasterFxParam(masterFxId, param, value);
      const node = engine.masterFxNodes?.[masterFxId];
      const fx = (state.masterFxChain || []).find((f) => f.id === masterFxId);
      if (node && fx) engine.applyFxParams(node, fx.plugin, { ...fx.params, [param]: value });
      return;
    }
    // Track FX chain — update track slot + track node.
    state.updateFxParam(trackId, fxId, param, value);
    const node = engine.trackFx?.[trackId]?.[fxId];
    const track = state.tracks.find((t) => t.id === trackId);
    const fx = track?.fxChain?.find((f) => f.id === fxId);
    if (node && fx) engine.applyFxParams(node, fx.plugin, { ...fx.params, [param]: value });
  };
  return apply;
}

function EqFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  return <EqFive trackId={trackId} fx={fx} apply={apply} />;
}

function CompressorFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params;
  return (
    <div className="grid grid-cols-2 gap-6 justify-items-center py-2">
      <Knob value={p.threshold} min={-60} max={0} step={0.5} label="Threshold" accentColor="#4682B4"
        onChange={(v) => apply("threshold", v)} format={(v) => `${v.toFixed(1)}dB`} />
      <Knob value={p.ratio} min={1} max={20} step={0.5} label="Ratio" accentColor="#4682B4"
        onChange={(v) => apply("ratio", v)} format={(v) => `${v.toFixed(1)}:1`} />
    </div>
  );
}

function DistortionFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params;
  return (
    <div className="grid grid-cols-2 gap-6 justify-items-center py-2">
      <Knob value={p.amount} min={0} max={1} step={0.01} label="Gain" accentColor="#007BFF"
        onChange={(v) => apply("amount", v)} format={(v) => `${Math.round(v * 100)}%`} />
      <Knob value={p.wet} min={0} max={1} step={0.01} label="Wet" accentColor="#007BFF"
        onChange={(v) => apply("wet", v)} format={(v) => `${Math.round(v * 100)}%`} />
    </div>
  );
}

function LimiterFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params;
  return (
    <div className="flex justify-center py-2">
      <Knob value={p.threshold} min={-24} max={0} step={0.5} label="Threshold" accentColor="#FF4500"
        onChange={(v) => apply("threshold", v)} format={(v) => `${v.toFixed(1)}dB`} />
    </div>
  );
}

function DelayFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params;
  // v9.9.8 — BPM-synced delay. `time` is a Tone.js notation string that
  // Tone.Time().toSeconds() converts against the CURRENT Tone.Transport.bpm
  // so the delay locks to project tempo. Producers pick the note value;
  // the engine handles resync when BPM changes (see AudioEngine.setBpm).
  const NOTE_OPTIONS = [
    { value: "2n",  label: "1/2"  },
    { value: "4n",  label: "1/4"  },
    { value: "8n",  label: "1/8"  },
    { value: "16n", label: "1/16" },
    { value: "32n", label: "1/32" },
  ];
  // Legacy saves stored `time` as a decimal number of seconds. Coerce so
  // the segmented selector still reflects a sensible active button — we
  // pick the note whose seconds-at-120bpm is closest to the stored ms.
  const currentTime = typeof p.time === "string"
    ? p.time
    : (() => {
        const secToNote = { 1.0: "2n", 0.5: "4n", 0.25: "8n", 0.125: "16n", 0.0625: "32n" };
        let best = "8n";
        let bestDiff = Infinity;
        Object.entries(secToNote).forEach(([sec, note]) => {
          const d = Math.abs((parseFloat(sec) || 0) - (Number(p.time) || 0));
          if (d < bestDiff) { bestDiff = d; best = note; }
        });
        return best;
      })();
  return (
    <div className="flex flex-col gap-4 py-2" data-testid={`fx-${fx.id}-delay-panel`}>
      <div>
        <div className="label-mono mb-2 text-center">Delay Time · BPM synced</div>
        <div className="flex justify-center gap-1" data-testid={`fx-${fx.id}-note-selector`}>
          {NOTE_OPTIONS.map((opt) => {
            const active = currentTime === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => apply("time", opt.value)}
                className="px-3 py-2 rounded font-mono text-xs font-bold transition"
                style={{
                  background: active ? "#B7AEEA" : "#0f0f0f",
                  color: active ? "#000" : "#B7AEEA",
                  border: `1.5px solid ${active ? "#B7AEEA" : "#B7AEEA55"}`,
                  boxShadow: active ? "0 0 12px #B7AEEAaa" : "none",
                  minWidth: 48,
                }}
                data-testid={`fx-${fx.id}-note-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6 justify-items-center">
        <Knob value={p.feedback} min={0} max={0.95} step={0.01} label="Feedback" accentColor="#B7AEEA"
          onChange={(v) => apply("feedback", v)} format={(v) => `${Math.round(v * 100)}%`} data-testid={`fx-${fx.id}-feedback`} />
        <Knob value={p.wet} min={0} max={1} step={0.01} label="Mix" accentColor="#B7AEEA"
          onChange={(v) => apply("wet", v)} format={(v) => `${Math.round(v * 100)}%`} data-testid={`fx-${fx.id}-wet`} />
      </div>
    </div>
  );
}

function ReverbFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params;
  return (
    <div className="grid grid-cols-2 gap-6 justify-items-center py-2">
      <Knob value={p.decay} min={0.1} max={10} step={0.1} label="Decay" accentColor="#D2691E"
        onChange={(v) => apply("decay", v)} format={(v) => `${v.toFixed(1)}s`} />
      <Knob value={p.wet} min={0} max={1} step={0.01} label="Wet" accentColor="#D2691E"
        onChange={(v) => apply("wet", v)} format={(v) => `${Math.round(v * 100)}%`} />
    </div>
  );
}

// Standalone "Wave View" plugin: shows the live master waveform + dB meter.
// Carries no params — the node itself is a transparent gain pass-through; the
// engine taps the master output into its private analyzers via connectTrackChain.
function WaveViewFx() {
  return (
    <div className="py-2 px-1" data-testid="waveview-fx-panel">
      <MasterWaveView height={120} />
      <div className="text-[10px] font-mono text-white/40 mt-2">
        Reads from the master bus — drop on any track to monitor the final mix.
      </div>
    </div>
  );
}

// v12.0 — Crow Pumper FX: rhythmic sidechain-style volume duck. Two
// controls only — Amount (0..100%) and Timing (1/2 · 1/4 · 1/8). Engine
// side handles the actual ramp scheduling on Tone.Transport ticks.
function PumperFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params || {};
  const mode = p.mode || "1/4";
  const modes = ["1/2", "1/4", "1/8"];
  return (
    <div className="flex flex-col items-center gap-4 py-2" data-testid={`fx-${fx.id}-pumper-panel`}>
      <div className="flex justify-center">
        <Knob
          value={p.amount ?? 0.6}
          min={0} max={1} step={0.01}
          label="Amount"
          accentColor="#F43F5E"
          onChange={(v) => apply("amount", v)}
          format={(v) => `${Math.round(v * 100)}%`}
          data-testid="pumper-amount-knob"
        />
      </div>
      <div className="w-full">
        <div className="label-mono mb-1 text-center">Timing</div>
        <div className="flex justify-center gap-1" data-testid="pumper-mode-selector">
          {modes.map((m) => (
            <button
              key={m}
              onClick={() => apply("mode", m)}
              className="px-3 py-1 text-[11px] font-mono rounded transition"
              style={{
                background: mode === m ? "#F43F5E" : "#1a1a1a",
                color: mode === m ? "#fff" : "#a3a3a3",
                border: `1px solid ${mode === m ? "#F43F5E" : "#2a2a2a"}`,
              }}
              data-testid={`pumper-mode-${m.replace("/", "-")}`}
            >{m}</button>
          ))}
        </div>
      </div>
      <div className="text-[10px] font-mono text-white/40 text-center max-w-[200px]">
        Rhythmic volume duck synced to project BPM. Classic sidechain pump without needing a source input.
      </div>
    </div>
  );
}

const FX_COMPONENTS = {
  eq: EqFx, compressor: CompressorFx, distortion: DistortionFx,
  limiter: LimiterFx, delay: DelayFx, reverb: ReverbFx,
  pumper: PumperFx,
  waveview: WaveViewFx,
  clipper: ClipperFx,
};

// v19.13 — Upward Clipper editor. 4 knobs mapped 1:1 to the FX_DEFS
// param schema in dawStore. Works for BOTH per-track and master
// contexts thanks to the (trackId | masterFxId) prop routing.
// v19.16 — Migrated onto the shared `makeApply(trackId, fxId, masterFxId)`
// helper so track-level knobs no longer throw
// `updateTrackFxParam is not a function` and no longer look up nodes on
// the wrong map (engine.fxNodes vs engine.trackFx). Every other FX
// editor already routes through makeApply — ClipperFx was the last
// hand-rolled setter.
function ClipperFx({ trackId, fx, masterFxId }) {
  const apply = makeApply(trackId, fx.id, masterFxId);
  const p = fx.params || {};
  return (
    <div className="grid grid-cols-4 gap-4 py-2" data-testid={`clipper-fx-${fx.id}`}>
      <Knob value={p.expandThreshold ?? -24} min={-60} max={0} step={0.5} label="THRESH" accentColor="#eab308"
        onChange={(v) => apply("expandThreshold", v)} format={(v) => `${v.toFixed(1)} dB`} data-testid="clipper-thresh" />
      <Knob value={p.expandAmount ?? 0.5} min={0} max={1} step={0.01} label="EXPAND" accentColor="#f97316"
        onChange={(v) => apply("expandAmount", v)} format={(v) => `${Math.round(v * 100)}%`} data-testid="clipper-expand" />
      <Knob value={p.clipDrive ?? 1.6} min={0.5} max={4} step={0.05} label="DRIVE" accentColor="#ef4444"
        onChange={(v) => apply("clipDrive", v)} format={(v) => `${v.toFixed(2)}x`} data-testid="clipper-drive" />
      <Knob value={p.clipCeiling ?? -0.3} min={-12} max={0} step={0.1} label="CEILING" accentColor="#22d3ee"
        onChange={(v) => apply("clipCeiling", v)} format={(v) => `${v.toFixed(1)} dB`} data-testid="clipper-ceiling" />
    </div>
  );
}

/* ---------- Galaxy Synth (track-aware) ---------- */
function GalaxySynth({ track }) {
  const waveforms = ["sine", "sawtooth", "triangle", "square"];
  // v9.9.10 — read voice config from the STORE (not local state) so the
  // producer's edits survive save/load AND WAV export. GalaxySynth used
  // to keep envelope in useState, which meant Tone.Offline rendered with
  // hardcoded defaults regardless of what the producer heard live.
  const setTrackGlide = useDAWStore((s) => s.setTrackGlide);
  const setTrackSynthConfig = useDAWStore((s) => s.setTrackSynthConfig);
  const cfg = track.synthConfig || {};
  const wave = cfg.oscType || "sawtooth";
  const adsr = cfg.envelope || { attack: 0.05, decay: 0.2, sustain: 0.4, release: 0.8 };
  const setWave = (w) => setTrackSynthConfig(track.id, { oscType: w });
  const setAdsr = (updater) => {
    const next = typeof updater === "function" ? updater(adsr) : updater;
    setTrackSynthConfig(track.id, { envelope: next });
  };
  const glide = track.glide ?? 0;

  React.useEffect(() => {
    const synth = engine.synthVoices?.[track.id];
    if (!synth) return;
    synth.set({
      oscillator: { type: wave },
      envelope: { attack: adsr.attack, decay: adsr.decay, sustain: adsr.sustain, release: adsr.release },
    });
  }, [wave, adsr.attack, adsr.decay, adsr.sustain, adsr.release, track.id]);

  return (
    <PluginShell name="Galaxy Synth" color={COLORS.synth} testId="plugin-galaxy-synth">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="label-mono mb-3">Waveform</div>
          <div className="grid grid-cols-2 gap-2">
            {waveforms.map((w) => (
              <button key={w} onClick={() => setWave(w)}
                className={`btn-3d py-3 text-xs font-semibold uppercase ${wave === w ? "active" : ""}`}
                style={wave === w ? { background: `linear-gradient(180deg, ${COLORS.synth}, #166534)`, color: "#fff", borderColor: COLORS.synth } : {}}
                data-testid={`synth-wave-${w}`}>{w}</button>
            ))}
          </div>
          {/* Glide (portamento) — how long the pitch smoothly slides between
              consecutive notes. 0 = classic step behaviour, 0.05-0.30s is a
              tasteful musical slide, higher values create dramatic sweeps. */}
          <div className="mt-6">
            <div className="label-mono mb-3">Glide</div>
            <div className="flex items-center gap-4">
              <Knob
                value={glide}
                min={0}
                max={2}
                step={0.005}
                size={44}
                label="TIME"
                accentColor={COLORS.synth}
                onChange={(v) => { setTrackGlide(track.id, v); engine.setTrackGlide(track.id, v); }}
                format={(v) => (v < 0.001 ? "off" : `${(v * 1000).toFixed(0)}ms`)}
                data-testid="synth-glide-knob"
              />
              <div className="text-[10px] font-mono text-white/40">
                double-click to reset · slides pitch between notes
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="label-mono mb-3">ADSR Envelope</div>
          <div className="grid grid-cols-2 gap-3">
            <Knob value={adsr.attack} min={0.001} max={2} step={0.01} label="Attack" accentColor={COLORS.synth}
              onChange={(v) => setAdsr((s) => ({ ...s, attack: v }))} format={(v) => `${v.toFixed(2)}s`} data-testid="synth-attack-knob" />
            <Knob value={adsr.decay} min={0.001} max={2} step={0.01} label="Decay" accentColor={COLORS.synth}
              onChange={(v) => setAdsr((s) => ({ ...s, decay: v }))} format={(v) => `${v.toFixed(2)}s`} data-testid="synth-decay-knob" />
            <Knob value={adsr.sustain} min={0} max={1} step={0.01} label="Sustain" accentColor={COLORS.synth}
              onChange={(v) => setAdsr((s) => ({ ...s, sustain: v }))} format={(v) => `${Math.round(v * 100)}%`} data-testid="synth-sustain-knob" />
            <Knob value={adsr.release} min={0.001} max={4} step={0.01} label="Release" accentColor={COLORS.synth}
              onChange={(v) => setAdsr((s) => ({ ...s, release: v }))} format={(v) => `${v.toFixed(2)}s`} data-testid="synth-release-knob" />
          </div>
        </div>
      </div>
    </PluginShell>
  );
}

/* ---------- Main view ---------- */
export default function PluginView() {
  const { tracks, selectedTrackId, selectTrack, removeFxFromTrack, activePlugin, masterFxChain, removeMasterFx } = useDAWStore();
  // v19.13 — Master FX mode. When `activePlugin` starts with "master:"
  // we render the master fxChain instead of the currently-selected
  // track's chain. This lets producers dial in the Upward Clipper on
  // the master bus without leaving the plugin panel.
  const isMasterMode = typeof activePlugin === "string" && activePlugin.startsWith("master:");
  const track = tracks.find((t) => t.id === selectedTrackId) || tracks[0];

  if (isMasterMode) {
    return (
      <div className="h-full overflow-auto p-4" data-testid="plugin-view-master">
        <div className="flex items-center justify-between mb-4 sticky top-0 z-10 bg-[#161616] py-2">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ background: "#00FF41", boxShadow: "0 0 8px #00FF41" }} />
            <span className="text-sm font-bold tracking-wider">MASTER BUS</span>
            <span className="label-mono">FX Chain</span>
            <span
              className="px-2 py-0.5 rounded font-mono text-[11px] font-bold border"
              style={{ color: (masterFxChain?.length || 0) > 0 ? "#3B82F6" : "#666", borderColor: (masterFxChain?.length || 0) > 0 ? "#3B82F6" : "#333" }}
            >{(masterFxChain?.length || 0)} / 5</span>
          </div>
          <div className="label-mono">Add FX from the Master strip in the Mixer</div>
        </div>
        {(masterFxChain || []).length === 0 ? (
          <div className="plugin-panel p-6 text-center text-white/40 text-sm mt-3">
            Add an FX from the Master strip's "ADD FX" picker.
          </div>
        ) : (
          <div className="mt-3">
            <div className="label-mono mb-2 px-1">Master signal chain (top to bottom)</div>
            {(masterFxChain || []).map((fx) => {
              const def = FX_DEFS[fx.plugin] || {};
              const Comp = FX_COMPONENTS[fx.plugin];
              return (
                <PluginShell
                  key={fx.id}
                  name={def.label || fx.plugin}
                  color={def.color || "#999"}
                  gradient={def.gradient}
                  onClose={() => removeMasterFx(fx.id)}
                  testId={`master-fx-panel-${fx.id}`}
                >
                  {Comp ? <Comp masterFxId={fx.id} fx={fx} /> : <div>—</div>}
                </PluginShell>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (!track) {
    return (
      <div className="h-full flex items-center justify-center text-white/40 text-sm" data-testid="plugin-view-empty">
        No track selected.
      </div>
    );
  }

  const fxChain = track.fxChain || [];

  return (
    <div className="h-full overflow-auto p-4" data-testid="plugin-view">
      {/* Header: track selector + FX count */}
      <div className="flex items-center justify-between mb-4 sticky top-0 z-10 bg-[#161616] py-2">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full" style={{ background: track.color, boxShadow: `0 0 8px ${track.color}` }} />
          <select
            value={track.id}
            onChange={(e) => selectTrack(e.target.value)}
            className="bg-[#0a0a0a] border border-[#222] rounded px-3 py-1.5 text-sm font-semibold focus:outline-none focus:border-[#444]"
            data-testid="plugin-track-select"
          >
            {tracks.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <span className="label-mono">FX Chain</span>
          <span
            className="px-2 py-0.5 rounded font-mono text-[11px] font-bold border"
            style={{
              color: fxChain.length > 0 ? "#3B82F6" : "#666",
              borderColor: fxChain.length > 0 ? "#3B82F6" : "#333",
              background: fxChain.length > 0 ? "rgba(59,130,246,0.1)" : "transparent",
            }}
            data-testid="plugin-fx-count"
          >{fxChain.length} / 5</span>
        </div>
        <div className="label-mono">Drag FX from sidebar onto track header</div>
      </div>

      {/* Instrument plugin */}
      {track.type === "synth" && <GalaxySynth track={track} />}
      {track.type === "sampler" && <Sampler />}

      {!track.type && (
        <div className="plugin-panel p-6 text-center text-white/40 text-sm" data-testid="plugin-no-instrument">
          No instrument on this track. Drag <span className="text-white/80">Galaxy Synth</span>, <span className="text-white/80">Sampler</span>, or a drum (Kick/Snare/Hi-Hat/Clap) from the sidebar onto this track header.
        </div>
      )}

      {/* FX chain list */}
      {fxChain.length === 0 ? (
        <div className="plugin-panel p-6 text-center text-white/40 text-sm mt-3" data-testid="plugin-no-fx">
          <div className="flex items-center justify-center gap-2 mb-2"><Plus size={14} weight="bold" />Drag any FX</div>
          <div className="label-mono">EQ · Compressor · Distortion · Limiter · Delay · Reverb (up to 5)</div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="label-mono mb-2 px-1">Signal chain (top to bottom)</div>
          {fxChain.map((fx) => {
            const def = FX_DEFS[fx.plugin] || {};
            const Comp = FX_COMPONENTS[fx.plugin];
            return (
              <PluginShell
                key={fx.id}
                name={def.label || fx.plugin}
                color={def.color || "#999"}
                gradient={def.gradient}
                onClose={() => removeFxFromTrack(track.id, fx.id)}
                testId={`fx-panel-${fx.id}`}
              >
                {Comp ? <Comp trackId={track.id} fx={fx} /> : <div>—</div>}
              </PluginShell>
            );
          })}
        </div>
      )}
    </div>
  );
}
